import { Button, Snackbar } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { sendShowImageCommandToChamberScreen } from 'src/consumer/services/featureServices'

// Create context and export it so other components can use it
export const ChamberScreenSignallingSnackBarContext = React.createContext()

export default function ChamberScreenSignallingSnackBarProvider({ children }) {
  const [snackBarDuration, setSnackBarDuration] = useState(0)
  const [snackBarMessage, setSnackBarMessage] = useState('')
  const [chamberRoomId, setChamberRoomId] = useState('')
  const [customActionComponent, setCustomActionComponent] = useState(null)
  const [showActionComponent, setShowActionComponent] = useState(false)
  const [sentSignalRelatedData, setSentSignalRelatedData] = useState(null)

  // This useEffect handles the countdown timer when duration > 0
  useEffect(() => {
    let timer = null

    if (snackBarDuration > 0) {
      timer = setInterval(() => {
        setSnackBarDuration((prevDuration) => {
          const newDuration = prevDuration - 1
          if (newDuration <= 0) {
            clearInterval(timer)
            return 0
          }
          return newDuration
        })
      }, 1000) // Update every second
    }

    return () => {
      if (timer) clearInterval(timer)
    }
  }, [snackBarDuration])

  // Function to show the snackbar with a message and duration
  const showSnackBar = (
    message,
    duration,
    roomId,
    customActionComponent,
    sentSignalRelatedData,
  ) => {
    setChamberRoomId(roomId)
    setSnackBarMessage(message)
    setSnackBarDuration(duration)
    setCustomActionComponent(customActionComponent)
    setSentSignalRelatedData(sentSignalRelatedData)
  }

  function cleanSnackBarData() {
    setSnackBarMessage('')
    setCustomActionComponent(null)
    setSentSignalRelatedData(null)
  }

  // Function to hide the snackbar
  const hideSnackBar = () => {
    setSnackBarDuration(0)
    cleanSnackBarData()
  }

  useEffect(() => {
    if (snackBarDuration <= 0) {
      cleanSnackBarData()
    }
  }, [snackBarDuration])

  return (
    <ChamberScreenSignallingSnackBarContext.Provider
      value={{ showSnackBar, hideSnackBar, snackBarDuration, sentSignalRelatedData }}
    >
      {createPortal(
        <Snackbar
          open={snackBarDuration > 0}
          autoHideDuration={null} // We're managing the duration ourselves
          message={`${snackBarMessage} (${snackBarDuration}s remaining)`}
          sx={{
            zIndex: 1400,
            '& .MuiSnackbarContent-root': {
              backgroundColor: 'white',
              color: '#6779e3',
            },
          }}
          action={
            customActionComponent ? (
              customActionComponent
            ) : (
              <Button
                size="small"
                color="primary"
                onClick={() => {
                  if (chamberRoomId) {
                    sendShowImageCommandToChamberScreen(chamberRoomId, 'clear-screen')
                  }
                  hideSnackBar()
                }}
              >
                Clear Room Screen
              </Button>
            )
          }
          key="chamber-action-snackbar"
        />,
        document.body,
      )}
      {children}
    </ChamberScreenSignallingSnackBarContext.Provider>
  )
}
